import React from 'react'
import {
  Body,
  Form,
  FormLongTextInput,
  FormSelectInput,
  FormTextInput,
  Modal,
} from '@superhi/design'
import { useMutation } from '@apollo/client'
import { createErrorMap } from '@superhi/utils'

import { FIELDS, VALIDATION_SCHEMA } from './config'
import { REGISTER_INTEREST } from './mutations'
import { PlanType } from './types'

import { API } from 'src/api'

type FormValues = {
  name: string
  companyName: string
  email: string
  size: number
  plan: PlanType
  details?: string
}

type Props = {
  planType: PlanType
  onClose: React.ComponentProps<typeof Modal>['onClose']
}

const SignUpModal: React.FC<React.PropsWithChildren<Props>> = ({ planType, onClose }) => {
  const [registerInterest, registerInterestResult] = useMutation<
    API.Teams_SignUpModal_RegisterInterest,
    API.Teams_SignUpModal_RegisterInterestVariables
  >(REGISTER_INTEREST)

  const success = !!registerInterestResult.data?.teamsRegisterInterest

  return (
    <Modal
      title={!success ? 'Contact us' : 'Thanks for your interest!'}
      onClose={onClose}
      align="center"
    >
      <Body level="3">
        {!success
          ? 'Tell us about your team and any specific needs or goals you have. We’ll get back to you shortly with a quote and further details.'
          : 'Someone from our team will be in contact shortly to discuss next steps in setting up your SuperHi For Teams plan.'}
      </Body>
      {!success && (
        <Form<FormValues>
          name="plans-sign-up"
          initialValues={{
            name: '',
            companyName: '',
            email: '',
            size: 2,
            plan: planType,
            details: '',
          }}
          validationSchema={VALIDATION_SCHEMA}
          submitButtonAlign="center"
          onSubmit={async (values, helpers) => {
            try {
              await registerInterest({
                variables: {
                  name: values.name,
                  from: values.email,
                  subject: `${values.companyName} | ${values.plan} | ${values.size} seats`,
                  body: `
                  <b>Company:</b> ${values.companyName}<br />
                  <b>Seats:</b> ${values.size}<br />
                  <b>Plan:</b> ${values.plan}<br />
                  <b>Details:</b><br />
                  ${values.details}
                `,
                },
              })
            } catch (e) {
              const errors = Object.entries(createErrorMap(e))

              if (errors.length) {
                errors.forEach(([name, error]) => {
                  helpers.setFieldError(name, error as string)
                })
              }
            }
          }}
        >
          <Form.Rows>
            <FormTextInput {...FIELDS.NAME} />
            <FormTextInput {...FIELDS.COMPANY_NAME} />
            <FormTextInput {...FIELDS.EMAIL} />
            <FormTextInput {...FIELDS.SIZE} />
            <FormSelectInput {...FIELDS.PLAN} />
            <FormLongTextInput {...FIELDS.DETAILS} />
          </Form.Rows>
        </Form>
      )}
    </Modal>
  )
}

export default SignUpModal
